import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { TabBar } from "../components/TabBar";
import { TopBar } from "../components/TopBar";
import { api } from "../api/client";
import type { ProgramView, WorkoutSession, WorkoutTemplate } from "../api/types";

export const Programs = () => {
  const [programs, setPrograms] = useState<ProgramView[]>([]);
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [tab, setTab] = useState<"programs" | "plans">("programs");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    const [ps, ts] = await Promise.all([
      api<ProgramView[]>("/programs"),
      api<WorkoutTemplate[]>("/templates"),
    ]);
    setPrograms(ps);
    setTemplates(ts);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const newProgram = async () => {
    setBusy(true);
    try {
      const p = await api<ProgramView>("/programs", {
        method: "POST",
        body: { name: "New program", plans: [] },
      });
      nav(`/programs/${p.id}`);
    } finally {
      setBusy(false);
    }
  };

  const newPlan = async () => {
    setBusy(true);
    try {
      const t = await api<WorkoutTemplate>("/templates", {
        method: "POST",
        body: { name: "New plan", exercises: [] },
      });
      nav(`/templates/${t.id}`);
    } finally {
      setBusy(false);
    }
  };

  const activate = async (id: string) => {
    await api(`/programs/${id}/activate`, { method: "POST" });
    await load();
  };

  const deactivate = async () => {
    await api(`/programs/active`, { method: "DELETE" });
    await load();
  };

  const reset = async (id: string) => {
    if (!confirm("Reset progress to the first plan?")) return;
    await api(`/programs/${id}/reset`, { method: "POST" });
    await load();
  };

  const startNext = async (p: ProgramView) => {
    if (p.plans.length === 0) return;
    const idx = p.state.currentIndex % p.plans.length;
    const templateId = p.plans[idx];
    setBusy(true);
    try {
      const sess = await api<WorkoutSession>("/sessions", {
        method: "POST",
        body: { templateId, programId: p.id },
      });
      nav(`/session/${sess.id}`);
    } finally {
      setBusy(false);
    }
  };

  const removeProgram = async (id: string) => {
    if (!confirm("Delete this program? (your plans stay)")) return;
    await api(`/programs/${id}`, { method: "DELETE" });
    await load();
  };

  const removePlan = async (id: string) => {
    if (!confirm("Delete this plan?")) return;
    await api(`/templates/${id}`, { method: "DELETE" });
    await load();
  };

  const tplName = (id: string) => templates.find((t) => t.id === id)?.name ?? id;

  return (
    <>
      <div className="page">
        <h1 className="h1">Workouts</h1>
        <div className="muted">Programs string plans together — start the next one whenever you're ready.</div>

        <div className="filters" style={{ marginTop: 12 }}>
          <span
            className={`chip ${tab === "programs" ? "active" : ""}`}
            onClick={() => setTab("programs")}
          >
            Programs
          </span>
          <span
            className={`chip ${tab === "plans" ? "active" : ""}`}
            onClick={() => setTab("plans")}
          >
            Plans
          </span>
        </div>

        {tab === "programs" ? (
          <>
            <button className="btn" disabled={busy} onClick={() => void newProgram()}>
              + New program
            </button>

            <div className="col" style={{ marginTop: 16 }}>
              {loading && <div className="muted">Loading…</div>}
              {!loading && programs.length === 0 && (
                <div className="muted">No programs yet. Create one to sequence your plans.</div>
              )}
              {programs.map((p) => {
                const len = p.plans.length;
                const idx = len ? p.state.currentIndex % len : 0;
                const nextName = len ? tplName(p.plans[idx]) : "No plans yet";
                return (
                  <div className="card" key={p.id}>
                    <div className="row">
                      <Link to={`/programs/${p.id}`} style={{ color: "inherit", flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>
                          {p.name} {p.active && <span className="chip solid">Active</span>}
                        </div>
                        <div className="muted">{len} plans</div>
                      </Link>
                      <button className="btn small secondary" onClick={() => void removeProgram(p.id)}>
                        ✕
                      </button>
                    </div>

                    {len > 0 && (
                      <>
                        <div className="previous-line" style={{ marginTop: 10 }}>
                          <strong>Next up:</strong> {nextName}{" "}
                          <span className="muted">
                            ({idx + 1}/{len})
                          </span>
                        </div>
                        <div className="row" style={{ marginTop: 10, gap: 8 }}>
                          <button className="btn" disabled={busy} onClick={() => void startNext(p)}>
                            Start next
                          </button>
                          {p.active ? (
                            <button className="btn secondary" onClick={() => void deactivate()}>
                              Unset active
                            </button>
                          ) : (
                            <button className="btn secondary" onClick={() => void activate(p.id)}>
                              Set active
                            </button>
                          )}
                          <button className="btn small secondary" onClick={() => void reset(p.id)}>
                            Reset
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <button className="btn" disabled={busy} onClick={() => void newPlan()}>
              + New plan
            </button>
            <div className="col" style={{ marginTop: 16 }}>
              {loading && <div className="muted">Loading…</div>}
              {!loading && templates.length === 0 && (
                <div className="muted">No plans yet. Plans are the building blocks of a workout session.</div>
              )}
              {templates.map((t) => (
                <div className="card" key={t.id}>
                  <div className="row">
                    <Link to={`/templates/${t.id}`} style={{ color: "inherit", flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{t.name}</div>
                      <div className="muted">{t.exercises.length} exercises</div>
                    </Link>
                    <button className="btn small secondary" onClick={() => void removePlan(t.id)}>
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <TabBar />
    </>
  );
};
